import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    LinearProgress,
    Paper,
    Typography,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import { fetchMyModules, fetchMyModuleDetail } from '../../api/api';

/**
 * The employee's training modules: name, progress and which posts remain.
 * Embedded, it renders nothing when no modules are assigned. With `standalone`
 * (the Training page) it shows a spinner and an empty state instead.
 */
const MyTraining = ({ refreshKey = 0, onOpenPost, standalone = false }) => {
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [details, setDetails] = useState({});

    const load = useCallback(async () => {
        try {
            const { data } = await fetchMyModules();
            setModules(data?.modules ?? []);
        } catch {
            setModules([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load, refreshKey]);

    const toggle = useCallback(
        async (moduleId) => {
            const next = expanded === moduleId ? null : moduleId;
            setExpanded(next);

            if (next && !details[next]) {
                try {
                    const { data } = await fetchMyModuleDetail(next);
                    setDetails((prev) => ({ ...prev, [next]: data?.posts ?? [] }));
                } catch {
                    setDetails((prev) => ({ ...prev, [next]: [] }));
                }
            }
        },
        [expanded, details]
    );

    if (standalone && loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (standalone && modules.length === 0) {
        return (
            <Paper
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 4, textAlign: 'center' }}
            >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No training modules have been assigned to you yet.
                </Typography>
            </Paper>
        );
    }

    // Nothing assigned: stay out of the way entirely
    if (loading || modules.length === 0) {
        return null;
    }

    return (
        <Box sx={{ mb: 3 }}>
            {!standalone && (
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Your training
                </Typography>
            )}

            {modules.map((module) => {
                const pct = module.total > 0 ? Math.round((module.read / module.total) * 100) : 0;
                const isComplete = module.state === 'completed';
                const isOpen = expanded === module.module_id;
                const posts = details[module.module_id];

                return (
                    <Paper
                        key={module.module_id}
                        elevation={0}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            p: 2,
                            mb: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap>
                                    {module.name}
                                </Typography>
                                {module.description && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {module.description}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                {isComplete ? (
                                    <Chip
                                        size="small"
                                        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                        label="Complete"
                                        sx={{
                                            backgroundColor: '#dcfce7',
                                            color: '#15803d',
                                            fontWeight: 600,
                                        }}
                                    />
                                ) : (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            fontVariantNumeric: 'tabular-nums',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {module.read} of {module.total} read
                                    </Typography>
                                )}
                                <IconButton
                                    size="small"
                                    onClick={() => toggle(module.module_id)}
                                    aria-label={isOpen ? 'Hide posts' : 'Show posts'}
                                >
                                    {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Box>
                        </Box>

                        <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                                mt: 1.25,
                                height: 6,
                                borderRadius: 99,
                                backgroundColor: '#eef2f7',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 99,
                                    backgroundColor: isComplete ? '#22c55e' : '#2563eb',
                                },
                            }}
                        />

                        <Collapse in={isOpen} unmountOnExit>
                            <Box sx={{ mt: 1.5 }}>
                                {posts === undefined ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={18} />
                                    </Box>
                                ) : (
                                    posts.map((post) => {
                                        const isRead = ['read', 'accepted'].includes(post.user_status);

                                        return (
                                            <Box
                                                key={post.id}
                                                onClick={
                                                    !isRead && onOpenPost
                                                        ? () => onOpenPost(post)
                                                        : undefined
                                                }
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    py: 0.6,
                                                    cursor: !isRead && onOpenPost ? 'pointer' : 'default',
                                                }}
                                            >
                                                {isRead ? (
                                                    <CheckCircleIcon
                                                        sx={{ color: '#22c55e', fontSize: 18 }}
                                                    />
                                                ) : (
                                                    <RadioButtonUncheckedIcon
                                                        sx={{ color: '#cbd5e1', fontSize: 18 }}
                                                    />
                                                )}
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: isRead ? 'text.primary' : 'text.secondary',
                                                        textDecoration: 'none',
                                                    }}
                                                >
                                                    {post.title}
                                                </Typography>
                                            </Box>
                                        );
                                    })
                                )}
                            </Box>
                        </Collapse>
                    </Paper>
                );
            })}
        </Box>
    );
};

export default MyTraining;
